<script lang="ts">
  import { Select, Button } from '@aatmanova/ui';
  import { Coins, User, Calendar, Package, Clock, ShoppingCart } from '@lucide/svelte';

  interface Props {
    isOpen: boolean;
    order: any;
    client: any;
    onClose: () => void;
    onSave: (updatedOrder: any) => void;
  }

  let { isOpen = $bindable(), order, client, onClose, onSave }: Props = $props();

  let selectedStatus = $state('PENDING');
  let savePending = $state(false);

  // Sync selectedStatus with order status when order prop changes
  $effect(() => {
    if (order) {
      selectedStatus = order.status;
    }
  });

  // Lock body scrolling when drawer is open
  $effect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  });
  
  function getStatusBadgeClass(status: string) {
    switch (status) {
      case 'PENDING':
        return 'badge-warning';
      case 'PROCESSING':
        return 'badge-info';
      case 'SHIPPED':
        return 'badge-primary';
      case 'DELIVERED':
        return 'badge-success';
      case 'CANCELLED':
        return 'badge-error';
      default:
        return 'badge-ghost';
    }
  }

  async function handleStatusUpdate(e: SubmitEvent) {
    e.preventDefault();
    if (!order) return;
    savePending = true;

    try {
      const res = await client.updateOrder(order.id, { status: selectedStatus });
      if (res && res.success) {
        onSave(res.data);
      }
    } catch (err: any) {
      console.error(err);
      alert(err.message || 'Failed to update order status');
    } finally {
      savePending = false;
    }
  }
</script>

{#if isOpen && order}
  <div class="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-end">
    <div class="w-full max-w-2xl bg-base-100 border-l border-base-300 h-full flex flex-col justify-between shadow-2xl overflow-hidden transition-all duration-300">
      
      <!-- Drawer Header -->
      <div class="p-6 border-b border-base-300 bg-base-200/50">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-xl font-bold tracking-tight text-base-content flex items-center gap-2">
              <span class="w-2.5 h-2.5 rounded-full bg-accent animate-pulse"></span>
              Order Details
            </h3>
            <p class="text-xs text-base-content/60 font-mono mt-1 font-semibold uppercase tracking-wider">
              ID: {order.id}
            </p>
          </div>
          <button onclick={onClose} class="btn btn-sm btn-circle btn-ghost text-base-content/75 hover:bg-base-300">✕</button>
        </div>
      </div>

      <!-- Drawer Body -->
      <div class="flex-1 overflow-y-auto p-6 space-y-6">
        
        <!-- Summary Cards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div class="card bg-base-200 border border-base-300 p-4 rounded-xl flex flex-row items-center gap-3">
            <div class="p-2.5 bg-warning/20 text-warning rounded-lg">
              <Clock class="w-5 h-5" />
            </div>
            <div>
              <p class="text-xs text-base-content/50 font-bold uppercase tracking-wider">Status</p>
              <span class="badge badge-sm font-bold uppercase mt-0.5 {getStatusBadgeClass(order.status)}">
                {order.status}
              </span>
            </div>
          </div>

          <div class="card bg-base-200 border border-base-300 p-4 rounded-xl flex flex-row items-center gap-3">
            <div class="p-2.5 bg-success/20 text-success rounded-lg">
              <Coins class="w-5 h-5" />
            </div>
            <div>
              <p class="text-xs text-base-content/50 font-bold uppercase tracking-wider">Total Value</p>
              <p class="font-bold text-base-content text-base mt-0.5">Rs. {Number(order.total).toFixed(2)}</p>
            </div>
          </div>
        </div>

        <!-- Section 1: Customer Details -->
        <div class="card bg-base-200 border border-base-300 shadow-sm rounded-2xl p-5 space-y-4">
          <h4 class="text-xs font-extrabold uppercase tracking-widest text-primary flex items-center gap-1.5 pb-2 border-b border-base-300/50">
            <User class="w-4 h-4" /> Customer & Checkout Info
          </h4>
          
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p class="text-xs font-semibold text-base-content/55">Customer Name</p>
              <p class="font-bold text-base-content mt-0.5">{order.user?.name || 'Customer'}</p>
            </div>
            <div>
              <p class="text-xs font-semibold text-base-content/55">Email Address</p>
              <p class="font-semibold text-base-content/85 mt-0.5">{order.user?.email || 'N/A'}</p>
            </div>
            <div>
              <p class="text-xs font-semibold text-base-content/55">Contact Number</p>
              <p class="font-bold text-base-content mt-0.5">{order.contactNo || 'N/A'}</p>
            </div>
            <div>
              <p class="text-xs font-semibold text-base-content/55">Checkout Date</p>
              <p class="font-semibold text-base-content/85 mt-0.5">
                {new Date(order.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
              </p>
            </div>
            <div class="sm:col-span-2">
              <p class="text-xs font-semibold text-base-content/55">Shipping Address</p>
              <p class="font-semibold text-base-content/85 mt-0.5 bg-base-100 p-2.5 rounded-xl border border-base-300/50 min-h-16 whitespace-pre-wrap leading-relaxed">
                {order.address || 'No shipping address provided.'}
              </p>
            </div>
          </div>
        </div>

        <!-- Section 2: Items Purchased -->
        <div class="card bg-base-200 border border-base-300 shadow-sm rounded-2xl p-5 space-y-4">
          <h4 class="text-xs font-extrabold uppercase tracking-widest text-primary flex items-center gap-1.5 pb-2 border-b border-base-300/50">
            <Package class="w-4 h-4" /> Ordered Products ({order.items?.length || 0})
          </h4>
          
          <div class="space-y-3">
            {#each order.items || [] as item}
              <div class="flex items-center gap-3 p-3 bg-base-100 rounded-xl border border-base-300/60">
                <div class="w-12 h-12 bg-base-200 rounded-lg overflow-hidden flex items-center justify-center border border-base-300 flex-shrink-0">
                  {#if item.product?.media?.[0]?.url}
                    <img src={item.product.media[0].url} alt={item.product.name} class="w-full h-full object-cover" />
                  {:else}
                    <ShoppingCart class="w-5 h-5 text-base-content/30" />
                  {/if}
                </div>
                <div class="flex-1 min-w-0">
                  <p class="font-bold text-xs text-base-content truncate">{item.product?.name || 'Unknown Product'}</p>
                  <p class="text-[10px] text-base-content/50 font-mono mt-0.5">SKU: {item.product?.sku || 'N/A'}</p>
                </div>
                <div class="text-right flex-shrink-0">
                  <p class="text-xs font-bold text-base-content">
                    Rs. {Number(item.price).toFixed(2)}
                  </p>
                  <p class="text-[10px] font-semibold text-base-content/50 mt-0.5">
                    Qty: {item.quantity}
                  </p>
                </div>
              </div>
            {/each}
          </div>
        </div>

        <!-- Section 3: Status Management Form -->
        <form id="orderStatusForm" onsubmit={handleStatusUpdate} class="card bg-base-200 border border-base-300 shadow-sm rounded-2xl p-5 space-y-4">
          <h4 class="text-xs font-extrabold uppercase tracking-widest text-primary flex items-center gap-1.5 pb-2 border-b border-base-300/50">
            <Clock class="w-4 h-4" /> Manage Order Workflow
          </h4>

          <div class="form-control w-full">
            <label for="orderStatusSelect" class="label">
              <span class="label-text text-xs font-bold text-base-content/75">Update Order Status</span>
            </label>
            <Select id="orderStatusSelect" bind:value={selectedStatus} class="select-bordered select-md w-full">
              <option value="PENDING">PENDING</option>
              <option value="PROCESSING">PROCESSING</option>
              <option value="SHIPPED">SHIPPED</option>
              <option value="DELIVERED">DELIVERED</option>
              <option value="CANCELLED">CANCELLED</option>
            </Select>
          </div>
        </form>

      </div>

      <!-- Drawer Footer -->
      <div class="p-6 border-t border-base-300 bg-base-200/50 flex items-center justify-end gap-3">
        <Button
          type="button"
          onclick={onClose}
          variant="outline"
          size="sm"
        >
          Close
        </Button>
        <Button
          type="submit"
          form="orderStatusForm"
          disabled={savePending}
          variant="primary"
          size="sm"
        >
          {#if savePending}
            Updating...
          {:else}
            Save Status
          {/if}
        </Button>
      </div>

    </div>
  </div>
{/if}
